'use client';

import NextLink from 'next/link';
import { usePathname } from 'next/navigation';

import { ChevronDown } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { type Locale, defaultLocale, locales, localeNames } from '@/lib/i18n';
import { rememberLocaleChoice } from '@/lib/i18n/locale-choice';
import { cn } from '@/lib/utils';

/**
 * Circular flags, drawn inline rather than as emoji: Windows ships no flag
 * glyphs, so 🇺🇸 degrades to the letters "US" there. Simplified on purpose —
 * at 18px the 50 stars and the shahada are a smudge either way.
 */
const FLAGS: Record<Locale, React.ReactNode> = {
  en: (
    <svg
      viewBox="0 0 20 20"
      className="size-[18px] shrink-0"
      aria-hidden="true"
    >
      <defs>
        <clipPath id="flag-circle-en">
          <circle cx="10" cy="10" r="10" />
        </clipPath>
      </defs>
      <g clipPath="url(#flag-circle-en)">
        <rect width="20" height="20" fill="#fff" />
        {[0, 2, 4, 6, 8, 10, 12].map((i) => (
          <rect
            key={i}
            y={(i * 20) / 13}
            width="20"
            height={20 / 13}
            fill="#b22234"
          />
        ))}
        <rect width="8.6" height={(7 * 20) / 13} fill="#3c3b6e" />
        {[
          [1.7, 1.6],
          [4.3, 1.6],
          [6.9, 1.6],
          [3, 3.9],
          [5.6, 3.9],
          [1.7, 6.2],
          [4.3, 6.2],
          [6.9, 6.2],
          [3, 8.5],
          [5.6, 8.5],
        ].map(([cx, cy]) => (
          <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="0.62" fill="#fff" />
        ))}
      </g>
    </svg>
  ),
  ar: (
    <svg
      viewBox="0 0 20 20"
      className="size-[18px] shrink-0"
      aria-hidden="true"
    >
      <defs>
        <clipPath id="flag-circle-ar">
          <circle cx="10" cy="10" r="10" />
        </clipPath>
      </defs>
      <g clipPath="url(#flag-circle-ar)">
        <rect width="20" height="20" fill="#006c35" />
        {/* Shahada, suggested rather than spelled: varied strokes on a
            baseline plus a few dots, which is what Arabic script looks like
            in silhouette. Uniform teeth read as a picket fence. */}
        <g fill="#fff">
          <rect x="3" y="8.4" width="14" height="0.75" rx="0.37" />
          {(
            [
              [3.6, 2.4, 0.8],
              [5.1, 1.4, 0.75],
              [6.3, 3.1, 0.8],
              [7.8, 1.6, 0.75],
              [9.1, 2.2, 0.8],
              [10.5, 1.3, 0.75],
              [11.7, 2.9, 0.8],
              [13.2, 1.5, 0.75],
              [14.5, 2.3, 0.8],
              [15.9, 1.4, 0.75],
            ] as const
          ).map(([x, h, w]) => (
            <rect key={x} x={x} y={8.4 - h} width={w} height={h} rx={w / 2} />
          ))}
          <circle cx="6.7" cy="4.3" r="0.42" />
          <circle cx="10.4" cy="4.6" r="0.42" />
          <circle cx="14.9" cy="4.3" r="0.42" />
        </g>
        {/* Sword: blade points left, hilt on the right, as on the flag. */}
        <g fill="#fff">
          <rect x="5.4" y="11.9" width="10.2" height="0.95" rx="0.4" />
          <path d="M5.6 11.75 2.7 12.38 5.6 13.01Z" />
          <rect x="15.1" y="10.9" width="0.85" height="3" rx="0.42" />
          <rect x="16.1" y="11.75" width="1.5" height="1.25" rx="0.55" />
        </g>
      </g>
    </svg>
  ),
};

/**
 * Language picker. The site has no other language control, so this is the only
 * way a visitor gets from /about to /ar/about without editing the URL bar.
 *
 * Each entry is a real anchor to the same page in the other language, not a
 * client-side locale toggle — the URL is the source of truth for locale (see
 * middleware), so a link is both correct and crawlable.
 *
 * The click also writes LOCALE_CHOICE, which is the only thing that stops the
 * Gulf geo-redirect. That makes this dropdown the opt-out for it: a Saudi
 * visitor is offered the Arabic site on every visit until they pick English
 * here, and never again afterwards.
 *
 * Driven off `locales`, so adding a third language needs no edit here (beyond
 * its flag).
 */
export function LocaleSwitch({
  dark = false,
  className,
}: {
  dark?: boolean;
  className?: string;
}) {
  const pathname = usePathname() || '/';

  // Strip any locale prefix so we always start from the unprefixed path,
  // which is what the default locale is served at.
  const base =
    pathname.replace(new RegExp(`^/(${locales.join('|')})(?=/|$)`), '') || '/';

  const hrefFor = (locale: Locale) =>
    locale === defaultLocale
      ? base
      : base === '/'
        ? `/${locale}`
        : `/${locale}${base}`;

  const matched = locales.find(
    (locale) =>
      locale !== defaultLocale &&
      (pathname === `/${locale}` || pathname.startsWith(`/${locale}/`)),
  );
  const active: Locale = matched ?? defaultLocale;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Change language"
          className={cn(
            'inline-flex cursor-pointer items-center gap-1.5 text-sm font-bold transition-colors',
            dark
              ? 'text-white/80 hover:text-white'
              : 'text-neutral-600 hover:text-neutral-900',
            className,
          )}
        >
          {FLAGS[active]}
          {/* The full name crowds a narrow header; the code does not. */}
          <span className="hidden md:inline" lang={active}>
            {localeNames[active]}
          </span>
          <span className="md:hidden">{active.toUpperCase()}</span>
          <ChevronDown className="size-3.5 shrink-0" />
        </button>
      </DropdownMenuTrigger>

      {/* Direction is inherited from the locale, so on /ar the flag column
          sits on the right — consistently, for every row. */}
      <DropdownMenuContent
        align="end"
        className="radius-12 min-w-48 shadow-none"
      >
        {locales.map((locale) => {
          const isActive = locale === active;
          return (
            <DropdownMenuItem
              key={locale}
              asChild
              className={cn(
                'cursor-pointer rounded-lg py-2.5 text-sm!',
                // --accent is 96% grey on a white popover, which reads as
                // nothing. The current language gets a solid bar instead, held
                // through focus so hover does not erase it.
                isActive &&
                  'bg-neutral-900 font-medium text-white focus:bg-neutral-900 focus:text-white',
              )}
            >
              <NextLink
                href={hrefFor(locale)}
                hrefLang={locale}
                lang={locale}
                onClick={() => rememberLocaleChoice(locale)}
                aria-current={isActive ? 'true' : undefined}
                className="flex w-full items-center gap-2.5 text-start"
              >
                {FLAGS[locale]}
                <span>{localeNames[locale]}</span>
              </NextLink>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LocaleSwitch;
